import { useState } from 'react';
import Table, { Column } from '../../../common/table';
import { useTranslation } from 'react-i18next';
import { formatToShortCst } from '../../../../utils/formatToShortCst';
import React from 'react';
import { Alert } from '../../../../types/Alert';
import { DeviceData } from '../../../../types/DeviceData';
import { Condition } from '../../../../types/Condition';

interface BloomTableProps {
  alerts: Alert[];
  deviceData: DeviceData[];
  highlightedRowId?: string;
  removeHighlightedRowId?: (id: string | null) => void;
}

const convertToOpenCloseComponent = (value: number) => {
  return value === 1 ? (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-left text-xs font-medium capitalize bg-green-100 text-green-800">
      Opened
    </span>
  ) : (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-left text-xs font-medium capitalize bg-red-100 text-red-800">
      Closed
    </span>
  );
}

export default function BloomTable({
  alerts,
  deviceData,
  highlightedRowId,
  removeHighlightedRowId,
}: BloomTableProps) {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState<{
    visible: boolean;
    content: any;
    top: number;
    left: number;
  }>({ visible: false, content: <>T</>, top: 0, left: 0 });

  const hasActiveAlert = (rowId: string) => {
    return alerts.some(
      (alert) => alert.deviceDataId === rowId && !alert.cleared,
    );
  };

  const handleMouseEnter = (row: any, rowElement: HTMLElement) => {
    const activeAlert = alerts.find(
      (alert) => alert.deviceDataId === row._id && !alert.cleared,
    );

    if (!hasActiveAlert(row._id)) return;

    const rect = rowElement.getBoundingClientRect();

    setHoveredRow({
      visible: true,
      content: (
        <div style={{ width: 'auto', height: 'auto' }}>
          <h1>
            {t('sites.type')}: {activeAlert?.triggerType}
          </h1>
          <h1>
            {t('sites.monitor')}: {activeAlert?.monitor?.name}
          </h1>
          <h1>
            {t('sites.condition')}:{' '}
            {findConditionName(
              // @ts-ignore
              activeAlert?.monitor?.conditions,
              activeAlert?.conditionId,
            )}
          </h1>
          <h1>
            {t('sites.priority')}: P{activeAlert?.priority}
          </h1>
        </div>
      ),
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleMouseLeave = () => {
    setHoveredRow({ ...hoveredRow, visible: false });
  };

  const findConditionName = (conditions: Condition[], conditionId: string) => {
    const condition = conditions?.find(
      (condition) => condition._id === conditionId,
    );

    return condition?.name;
  }

  const columns: Column<any>[] = [
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.createdAt')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {formatToShortCst(row.createdAt)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          DI 1
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {convertToOpenCloseComponent(row.data?.di1)}
          </td>
        );
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          DI 2
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const rowRef = React.createRef<HTMLTableCellElement>();

        return (
          <td
            key={colI}
            ref={rowRef}
            className={`hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 ${
              hasActiveAlert(row._id) ? 'bg-red-100 text-blue-800' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(row, rowRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {convertToOpenCloseComponent(row.data?.di2)}
          </td>
        );
      },
    },
  ];

  return (
    <>
      <Table
        removePadding
        columns={columns}
        rows={deviceData.sort(
          (a, b) =>
            // @ts-ignore
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )}
        title={t('sites.deviceData')}
        currentPage={1}
        pageSize={1}
        totalRows={1}
        highlightedRowId={highlightedRowId}
        setHighlightedRowId={removeHighlightedRowId}
      />
    </>
  );
}
